// THE MOON IS ROUND — a render-time curvature for the Brick Moon.
//
// The physics out here lives on an UNROLLED cylinder: x is longitude measured
// at the datum, y is altitude, z runs along the axis, and the kernel
// (internal/orbit, client/src/orbit_core.js) integrates that flat coordinate
// system bit-for-bit on both ends of the wire. That model is load-bearing and
// it is not up for discussion — it is what makes a Kepler orbit cheap enough
// to predict on a phone and identical on the server.
//
// But it was also what the player SAW. Drawn literally, an unrolled world is
// an infinite plain: the mare ran to the far plane in every direction, the
// horizon was a straight line at the edge of the draw distance, and the sun
// set through the ground because the ground simply stopped before the sun did.
// "It stretches out forever and doesn't feel round", verbatim, and the verdict
// was right.
//
// So the RENDERER bends. Every vertex of every space material is mapped onto a
// sphere of the world's own radius (R = wrap / 2π ≈ 16 km) about the camera:
// the point directly under the eye is untouched, and everything else rolls
// away and down exactly as fast as a moon that size demands. The horizon
// arrives where the arithmetic says it should — √(2Rh) is 3.6 km from a 400 m
// orbit — the limb is a curve, terrain sets behind the bulge, and the sun goes
// down behind rock instead of through it.
//
// (This file is also what proved the moon was too SMALL. Drawn round for the
// first time, 7.2 km of circumference read as a pebble you could see all of at
// once — and the moon it draws is fourteen times bigger for it.)
//
// Two honest notes on what this is and isn't:
//
//   • It is a CAMERA-RELATIVE map, not a re-projection of the world into
//     spherical coordinates. Nothing in the simulation moves. The pixels bend;
//     the physics, the wire, the collision, the fog of war and the fire
//     control all still run on the flat cylinder, which is why this file can
//     exist at all without touching a parity mirror.
//   • The physics cylinder is straight along z, and this draws a SPHERE, so a
//     contact far out on the cross-track axis renders a little lower than a
//     pedantic unrolling would put it (~107 m at 500 m of z). That is a
//     deliberate trade: z excursions in play are a couple of hundred metres,
//     a barrel-shaped moon with an endless axis would keep exactly the feeling
//     this file exists to kill, and — the part that matters — the SAME map is
//     applied to the terrain, the hulls, the tracers and the HUD's projected
//     marks, so what you aim at and what you hit stay the same pixel.
//
// Everything that belongs to the sky rather than the world (the star dome, the
// sun block and its glow, the far moon) opts out with
// `material.userData.noCurve = true` — those are at infinity, and bending them
// would drag the sun under the horizon and leave it there.

import * as THREE from "three";

// The shared uniforms: one camera position, one radius, one on/off. Every
// patched material points at these objects, so a single write per frame moves
// the whole world.
const uCurveCam = { value: new THREE.Vector3() };
const uCurveR = { value: 16043 };
const uCurveOn = { value: 0 };

/** Arm the curvature for a world of this wrap (metres). Space only. */
export function setCurveRadius(wrapM) {
  uCurveR.value = wrapM > 0 ? wrapM / (2 * Math.PI) : 16043;
  uCurveOn.value = 1;
}

/** The camera's unrolled position — the one point the map leaves alone. */
export function setCurveCamera(x, y, z) {
  uCurveCam.value.set(x, y, z);
}

export const curveRadius = () => uCurveR.value;
export const curveArmed = () => uCurveOn.value > 0.5;

// The GLSL, written once and shared by every patched material. `cwTheta` is
// the angle the point subtends around the moon from under the camera, and
// `cwBend` is the map: swing out along the ground track by r·sinθ, and fall
// away from the tangent plane by r·cosθ − r_cam. At θ = 0 it is the identity,
// which is why the cockpit never distorts.
const CURVE_GLSL = /* glsl */ `
uniform vec3 uCurveCam;
uniform float uCurveR;
uniform float uCurveOn;

// The horizon angle pair: two points on a sphere of radius R see each other
// exactly while the angle between them is under acos(R/r1) + acos(R/r2). It is
// the same chord test the server runs for spotting (spaceLOSClear) — the fog of
// war and the picture agree on where the world ends, which is the only way
// "it went behind the horizon" can ever be true of both.
float cwHorizonAngle(float rp, float rc) {
  return acos(clamp(uCurveR / rc, 0.0, 1.0)) + acos(clamp(uCurveR / rp, 0.0, 1.0));
}

vec3 cwBend(vec3 p) {
  vec2 d = vec2(p.x - uCurveCam.x, p.z - uCurveCam.z);
  float horiz = length(d);
  if (horiz < 0.001) return p;
  float r = uCurveR + p.y;
  float rc = uCurveR + uCurveCam.y;
  // Fold anything past the limb ONTO the limb. Without this, ground half a
  // moon away (θ > π/2 — and the far field spans a whole wrap plus 12 km of
  // axis, so θ ran past 10 radians) wraps around the sphere and comes back as
  // stretched shards standing over the horizon: the first curved build looked
  // like a field of spikes, and that was the mare's far side arriving
  // front-first. The fragment stage discards these anyway; the clamp is what
  // keeps the triangles that straddle the limb from spanning the planet.
  float theta = min(horiz / uCurveR, cwHorizonAngle(r, rc));
  vec2 out2 = d / horiz * (r * sin(theta));
  return vec3(
    uCurveCam.x + out2.x,
    uCurveCam.y + (r * cos(theta) - rc),
    uCurveCam.z + out2.y
  );
}

// Is this point over the camera's horizon? Passed to the fragment stage so the
// limb is a clean edge instead of a smear of folded geometry.
float cwHidden(vec3 p) {
  vec2 d = vec2(p.x - uCurveCam.x, p.z - uCurveCam.z);
  float theta = length(d) / uCurveR;
  return step(cwHorizonAngle(uCurveR + p.y, uCurveR + uCurveCam.y) + 0.004, theta);
}

// The rotation the map applies to a direction at that point: the local up
// tilts by theta toward the camera, around the axis perpendicular to the
// ground track. Normals need it or a round moon shades like a flat one.
vec3 cwBendNormal(vec3 p, vec3 n) {
  vec2 d = vec2(p.x - uCurveCam.x, p.z - uCurveCam.z);
  float horiz = length(d);
  if (horiz < 0.001) return n;
  float theta = horiz / uCurveR;
  vec2 u = d / horiz;                       // the ground-track direction
  float c = cos(theta), s = sin(theta);
  float along = dot(n.xz, u);               // n resolved along the track
  vec3 perp = vec3(n.x - along * u.x, 0.0, n.z - along * u.y); // and across it
  // Rotate the (along, up) pair by theta; the across component is untouched.
  float a2 = along * c + n.y * s;
  float y2 = -along * s + n.y * c;
  return normalize(vec3(perp.x + a2 * u.x, y2, perp.z + a2 * u.y));
}
`;

const patched = new WeakSet();

/**
 * Bend one material. Idempotent, and a no-op for anything flagged
 * `userData.noCurve` (the sky) or already patched.
 *
 * The injection replaces three of three.js's vertex chunks: the normal setup
 * (so lighting follows the curve), the world-position hook, and the
 * projection. Everything else about the material — vertex colours, fog,
 * transparency, instancing — is left exactly as it was.
 */
export function curveMaterial(mat) {
  if (!mat || patched.has(mat) || mat.userData?.noCurve) return mat;
  patched.add(mat);
  const prev = mat.onBeforeCompile;
  mat.onBeforeCompile = (shader, renderer) => {
    prev?.(shader, renderer);
    shader.uniforms.uCurveCam = uCurveCam;
    shader.uniforms.uCurveR = uCurveR;
    shader.uniforms.uCurveOn = uCurveOn;
    shader.vertexShader = shader.vertexShader.replace(
      "void main() {",
      `${CURVE_GLSL}\nvoid main() {`,
    );
    // Normals: rotate in WORLD space, then hand three.js the view-space normal
    // it expects. (The flat path is bit-identical when uCurveOn is 0.)
    if (shader.vertexShader.includes("#include <defaultnormal_vertex>")) {
      shader.vertexShader = shader.vertexShader.replace(
        "#include <defaultnormal_vertex>",
        `#include <defaultnormal_vertex>
        {
          // Same instancing rule as the projection below: the point whose
          // curvature we sample has to be where the instance actually IS, or a
          // thousand instanced blocks all shade as if they sat under the eye.
          vec4 cwNL = vec4(position, 1.0);
          #ifdef USE_BATCHING
            cwNL = batchingMatrix * cwNL;
          #endif
          #ifdef USE_INSTANCING
            cwNL = instanceMatrix * cwNL;
          #endif
          vec3 cwP = (modelMatrix * cwNL).xyz;
          vec3 cwNw = normalize(mat3(modelMatrix) * objectNormal);
          vec3 cwBent = cwBendNormal(cwP, cwNw);
          vec3 cwView = normalize(mat3(viewMatrix) * mix(cwNw, cwBent, uCurveOn));
          transformedNormal = cwView;
          #ifdef FLIP_SIDED
            transformedNormal = - transformedNormal;
          #endif
        }`,
      );
    }
    // Replacing <project_vertex> means we inherit its JOB, all of it. The stock
    // chunk folds the per-instance and per-batch matrices in before the model
    // matrix, and a curvature that forgets them sends every instance of an
    // InstancedMesh to the same place: the mesh's own origin. That is not a
    // subtle wrongness — tracers, impact sparks, explosion glow, flares and
    // rod trails are ALL instanced out here, so the whole war goes silent at
    // once. Guns that fire, hit, and kill with nothing on screen read as guns
    // that are broken, by the player and by the bots' apparent pacifism, and
    // that is exactly how it was reported. Keep this stanza in step with
    // three's project_vertex.glsl.js.
    shader.vertexShader = shader.vertexShader.replace(
      "#include <project_vertex>",
      `vec4 cwLocal = vec4(transformed, 1.0);
      #ifdef USE_BATCHING
        cwLocal = batchingMatrix * cwLocal;
      #endif
      #ifdef USE_INSTANCING
        cwLocal = instanceMatrix * cwLocal;
      #endif
      vec4 cwWorld = modelMatrix * cwLocal;
      vCurveHide = cwHidden(cwWorld.xyz) * uCurveOn;
      cwWorld.xyz = mix(cwWorld.xyz, cwBend(cwWorld.xyz), uCurveOn);
      vec4 mvPosition = viewMatrix * cwWorld;
      gl_Position = projectionMatrix * mvPosition;`,
    );
    // The limb, cut clean: anything the horizon test says is over the bulge
    // never reaches the screen. This is the line that turns "an infinite plain
    // that fades out" into "a moon that ends".
    shader.vertexShader = `varying float vCurveHide;\n${shader.vertexShader}`;
    shader.fragmentShader = shader.fragmentShader.replace(
      "void main() {",
      `varying float vCurveHide;\nvoid main() {\n  if (vCurveHide > 0.5) discard;`,
    );
    // Anything that wants the world position (fog, some sprites) should get
    // the bent one too, or it will fog by a distance nothing else agrees with.
    shader.vertexShader = shader.vertexShader.replace(
      "#include <worldpos_vertex>",
      `#if defined( USE_ENVMAP ) || defined( DISTANCE ) || defined ( USE_SHADOWMAP ) || defined ( USE_TRANSMISSION ) || NUM_SPOT_LIGHT_COORDS > 0
        vec4 worldPosition = cwWorld;
      #endif`,
    );
    mat.userData.curved = true;
  };
  mat.needsUpdate = true;
  return mat;
}

/**
 * Walk a scene graph and bend everything in it — the blunt instrument, and the
 * right one: the space theater builds meshes from a dozen modules (hulls,
 * props, tracers, ordnance, debris, effects) and a curvature that covers all
 * but one of them is worse than none, because the one that stays flat is the
 * one the eye catches. Cheap enough for a slow tick: a WeakSet means each
 * material is compiled once, however often this runs.
 *
 * Frustum culling goes off at the same time. Three.js culls against a bounding
 * sphere computed from the FLAT geometry, and a bent chunk can sit hundreds of
 * metres from where its bounds claim — which shows up as terrain popping in
 * and out along the limb.
 */
export function curveScene(root) {
  root.traverse((o) => {
    const m = o.material;
    if (!m) return;
    if (Array.isArray(m)) m.forEach(curveMaterial);
    else curveMaterial(m);
    if (o.isMesh || o.isLine || o.isPoints) {
      if (!m.userData?.noCurve && !(Array.isArray(m) && m[0]?.userData?.noCurve)) {
        o.frustumCulled = false;
      }
    }
  });
}

/** Mark a material (or object's material) as sky: at infinity, never bent. */
export function keepFlat(obj) {
  const m = obj?.material ?? obj;
  if (!m) return obj;
  (Array.isArray(m) ? m : [m]).forEach((mm) => {
    mm.userData = mm.userData ?? {};
    mm.userData.noCurve = true;
  });
  return obj;
}

/**
 * The CPU mirror of cwBend, for everything the HUD projects to the screen: the
 * lead pip, the tally diamonds, the rod ring, any marker that has to land on
 * the same pixel as the hull it belongs to. Writes into `out` and returns it.
 */
export function bendPoint(out, x, y, z) {
  if (uCurveOn.value < 0.5) return out.set(x, y, z);
  const cam = uCurveCam.value;
  const R = uCurveR.value;
  const dx = x - cam.x, dz = z - cam.z;
  const horiz = Math.hypot(dx, dz);
  if (horiz < 0.001) return out.set(x, y, z);
  const theta = horiz / R;
  const r = R + y;
  const k = (r * Math.sin(theta)) / horiz;
  return out.set(
    cam.x + dx * k,
    cam.y + (r * Math.cos(theta) - (R + cam.y)),
    cam.z + dz * k,
  );
}

/**
 * How far the horizon is, in metres of ground track, from this altitude — the
 * number that says whether the curvature is doing its job. √(2Rh + h²) is the
 * schoolbook answer and it is brutal on a moon this size: 900 m from a 300 m
 * orbit, 1.8 km from a kilometre up.
 */
export function horizonDistance(altM) {
  const R = uCurveR.value;
  return Math.sqrt(Math.max(0, 2 * R * altM + altM * altM));
}
